<template>
  <div class="relative flex w-full">
    <div
      class="relative flex flex-1 min-w-0 flex-col"
      data-cy="placement-container"
    >
      <floating-submit-section
        data-cy="floating-submit-section"
        :selected-funders="selectedFunders"
        :filters="filters"
        :selected-rows-by-funder="selectedRowsByFunder"
        :row-states-by-funder="rowStatesByFunder"
        :template-data-by-funder="templateDataByFunder"
        @application-submitted="handlePlacementSubmitted"
        @close="clearSelectedRows"
        @clear-successful-rows="clearSuccessfulRows"
        @update:filters="filters = $event"
        @update:submission-types="alignSubmissionsWithSelectedSubmissionTypes"
      />

      <template v-if="canViewPlacements">
        <div
          ref="submittedPlacementsRef"
          class="sticky z-10 -mx-8 bg-disabled px-8 py-4 xl:top-[66px] flex items-center justify-between"
          data-cy="submitted-placements-section"
        >
          <div class="flex items-center space-x-5 uppercase">
            <lf-h3>{{ $t("DEALS.PLACEMENT.SUBMITTED_PLACEMENTS") }}</lf-h3>

            <div class="flex items-center gap-2 flex-wrap">
              <div
                class="rounded-full border flex items-center px-2 py-0.5 font-semibold text-label whitespace-nowrap bg-white"
                data-cy="total-products-submitted-tag"
              >
                {{
                  $t(
                    "DEALS.PLACEMENT.PLACEMENTS_SUBMITTED",
                    { count: totalProductsSubmitted },
                    totalProductsSubmitted
                  )
                }}
              </div>

              <template
                v-for="[type, count] in placementsCountData"
                :key="type"
              >
                <funder-placement-count-tag v-if="count" :type :count />
              </template>
            </div>
          </div>

          <expand-button
            is-open
            @click="collapseAllSubmittedPlacementsTables"
          />
        </div>
        <div
          v-if="filteredPlacementsByFunder.length"
          class="mb-8 space-y-4"
          data-cy="submitted-placements-list"
        >
          <funder-placements-table
            v-for="{
              funderName,
              funderLogo,
              placements,
            } in filteredPlacementsByFunder"
            :key="funderName"
            ref="funderPlacementsTables"
            :funder-name="funderName"
            :funder-logo="funderLogo"
            :funder-placements="placements"
            :filters="filters"
          />
        </div>

        <div
          v-else
          class="flex w-full flex-col items-center justify-center space-y-2"
          data-cy="submitted-placements-empty-state"
        >
          <span class="font-semibold text-gray-600">
            {{ $t("DEALS.PLACEMENT.EMPTY_STATE_TEXT") }}
          </span>
          <span>{{ $t("DEALS.PLACEMENT.EMPTY_STATE_TEXT_ADDITIONAL") }}</span>
        </div>
      </template>

      <div
        ref="availableFundersRef"
        class="sticky z-20 -mx-8 bg-disabled px-8 py-4 xl:top-[66px] flex items-center justify-between"
        data-cy="available-funders-section"
      >
        <lf-h3 id="placement-available-funders-header">
          {{ $t("DEALS.PLACEMENT.AVAILABLE_FUNDERS_FOR_PLACEMENT") }}
        </lf-h3>

        <expand-button
          id="placement-available-funders-collapse-btn"
          is-open
          @click="collapseAllAvailableFundersTables"
        />
      </div>
      <div id="funders-table" class="space-y-1" data-cy="funders-container">
        <funder-placement-table
          v-for="funder in funders"
          :key="funder.id"
          ref="funderTableRefs"
          :funder
          :can-create-placement
          :funder-placement-presets-map
          :scorecard-results
          :filters
          :initial-selected-rows="selectedRowsByFunder.get(funder.id) || []"
          :initial-row-states="rowStatesByFunder.get(funder.id) || {}"
          @selected-rows="
            (funderId, selectedRows, rowStatesData, selectedTemplateData) =>
              handleSelectedRows(
                funderId,
                selectedRows,
                rowStatesData,
                selectedTemplateData
              )
          "
        />
      </div>
    </div>

    <placement-content-scroll-tracker
      id="placement-anchor-navigation"
      class="sticky top-15 2xl:top-30 z-50"
      data-cy="placement-scroll-tracker"
      :sections="placementContentSections"
      :scroll-container="anchorScrollContainer"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { onBeforeRouteLeave } from "vue-router";
import { useStore } from "vuex";

import orderBy from "lodash/orderBy";

import { AnalyticsEvent } from "@/enums/analytics";
import { Ability, PermissionSubject } from "@/enums/auth";
import { filterMap } from "@/helpers/common";
import { PLACEMENT_METHOD } from "@/helpers/constants";
import { SCORECARD_STATUS_APPROVED } from "@/helpers/constants/scorecards";
import { formatUserFullName } from "@/helpers/formatting";
import { isValidForSubmittedPlacements } from "@/helpers/placements";
import { usePermissions } from "@/hooks/auth";
import { usePromiseWrapper, useTour } from "@/hooks/common";
import { useDeals } from "@/hooks/deals";
import { usePlacementFundersQuery } from "@/hooks/queries/funders";
import { useActiveStage, useActiveWorkflowTemplate } from "@/hooks/workflow";
import {
  type IFunder,
  type ILenderPlacement,
  PlacementStatuses,
} from "@/models/funders";
import applicationsApiService from "@/services/modules/applications";
import keyValueService, { KeyId } from "@/services/modules/keyValue";
import { useAnalyticsStore } from "@/stores/analytics";

import FloatingSubmitSection from "@/components/deals/placement/FloatingSubmitSection.vue";
import FunderPlacementCountTag, {
  type PlacementCountType,
} from "@/components/deals/placement/FunderPlacementCountTag.vue";
import FunderPlacementTable from "@/components/deals/placement/FunderPlacementTable.vue";
import FunderPlacementsTable from "@/components/deals/placement/FunderPlacementsTable.vue";
import PlacementContentScrollTracker from "@/components/deals/placement/PlacementContentScrollTracker.vue";
import ExpandButton from "@/components/ui/ExpandButton.vue";
import LfH3 from "@/components/ui/text/LfHeading3.vue";

import type {
  AnchorNavigationFunder,
  AnchorNavigationProduct,
  AnchorNavigationSection,
} from "@/components/deals/placement/PlacementContentAnchorNavigation.vue";
import type { UiStatusColor } from "@/models/common";
import type { IPlacementTableRow } from "@/models/offers";
import type { IOptions, ProductType, RiskTier } from "@/models/options";
import type {
  EnrichedTableRow,
  PlacementFilters,
  RowState,
} from "@/models/placement";
import type { IScorecardResult } from "@/models/scorecards";

const anchorScrollContainer = ref<HTMLElement | null>(null);

const { activeStage } = useActiveStage();
const { funderPlacementPresetsMap, allSelectedOfferFunders } =
  useActiveWorkflowTemplate();
const { activeDeal } = useDeals();
const { dispatch, getters } = useStore();
const { trackEvent } = useAnalyticsStore();
const { canPerformActionReactive } = usePermissions();
const { t } = useI18n();
const submittedPlacementsEl = useTemplateRef("submittedPlacementsRef");
const availableFundersEl = useTemplateRef("availableFundersRef");
const submittedPlacementsTables = useTemplateRef("funderPlacementsTables");
const funderTableComponents = useTemplateRef("funderTableRefs");

const { nextTourStep, removeCurrentStep } = useTour({
  steps: [
    {
      title: t("DEALS.PLACEMENT.ONBOARDING.TAGS.TITLE"),
      description: t("DEALS.PLACEMENT.ONBOARDING.TAGS.DESCRIPTION"),
      selector: "#placement-available-funders-header",
      placement: "top",
    },
    {
      title: t("DEALS.PLACEMENT.ONBOARDING.ANCHOR.TITLE"),
      description: t("DEALS.PLACEMENT.ONBOARDING.ANCHOR.DESCRIPTION"),
      selector: "#placement-anchor-navigation",
      placement: "left",
    },
    {
      title: t("DEALS.PLACEMENT.ONBOARDING.TABLE_COLLAPSE.TITLE"),
      description: t("DEALS.PLACEMENT.ONBOARDING.TABLE_COLLAPSE.DESCRIPTION"),
      selector: "#placement-available-funders-collapse-btn",
      placement: "left",
    },
  ],
  onTourEnd: () => {
    keyValueService.kvRequest(KeyId.PlacementWflOnboardingTourSeen, true);
  },
});

const selectedFunderIds = ref<IFunder["id"][]>([]);
const selectedRowsByFunder = ref<Map<string, string[]>>(new Map());
const rowStatesByFunder = ref<Map<string, Record<string, RowState>>>(new Map());
const templateDataByFunder = ref<Map<string, EnrichedTableRow[]>>(new Map());
const filters = ref<PlacementFilters>({
  selectedProductTypes: [],
  selectedRiskTiers: [],
  searchString: "",
  selectedStatuses: [],
});
const funderIds = ref<string[]>([]);
const funders = ref<IFunder[]>([]);
const scorecardResults = ref<IScorecardResult[]>([]);

const placementContentSections = ref<AnchorNavigationSection[]>([]);

const scorecardResultsByFunderId = computed(() => {
  const map = new Map<string, IScorecardResult[]>();
  scorecardResults.value.forEach((sc) => {
    const funderId = (sc.score_card?.funder as IFunder)?.id;
    if (funderId) {
      const existing = map.get(funderId);
      if (existing) {
        existing.push(sc);
      } else {
        map.set(funderId, [sc]);
      }
    }
  });
  return map;
});

const PERMISSION_SUBJECTS = computed(() => [
  {
    subject: PermissionSubject.lenderPlacement,
    ability: Ability.viewAny,
    payload: { application: activeDeal.value.id },
  },
  {
    subject: PermissionSubject.lenderPlacement,
    ability: Ability.create,
    payload: {
      application: activeDeal.value.id,
      stageId: activeStage.value?.id,
    },
  },
]);

const placements = computed<ILenderPlacement[]>(
  () => getters["applications/placements"]
);

const submissionTypes = computed(
  () => (getters["options/all"] as IOptions)["placement"]?.["methods"]
);

const [canViewPlacements, canCreatePlacement] = PERMISSION_SUBJECTS.value.map(
  ({ subject, ability, payload }) =>
    canPerformActionReactive(subject, ability, payload)
);

const selectedFunders = computed(() =>
  allFunders.value.filter(({ id }) => selectedFunderIds.value.includes(id))
);

const fundersWithApprovedScorecards = computed(() => {
  const set = new Set<string>();
  scorecardResults.value.forEach((sc) => {
    const funderId = (sc.score_card?.funder as IFunder)?.id;
    if (funderId && sc.score_card?.status === SCORECARD_STATUS_APPROVED) {
      set.add(funderId);
    }
  });
  return set;
});

const fundersQueryParams = computed(() => ({ ids: funderIds.value }));
const fundersQuery = usePlacementFundersQuery(() => fundersQueryParams.value);
const fundersError = computed(() => fundersQuery.error.value);

const allFunders = computed(() => {
  if (!fundersQuery.data.value || fundersError.value) {
    return [];
  }

  const processedFunders = (fundersQuery.data.value || []).map((funder) => {
    const enrichedFunder = {
      ...funder,
      workflow_template_id: undefined,
      offer_template: undefined,
      client_offer_template: undefined,
      selected_method: funder.primary_method,
    };
    if (fundersWithApprovedScorecards.value.has(funder.id)) {
      return enrichFunderWithScorecardResults(enrichedFunder, true);
    }
    return enrichedFunder;
  });

  return sortFundersByScore(processedFunders);
});

const enrichPlacementWithScorecardResults = (placement: ILenderPlacement) => {
  const funderId = placement.funder?.id;
  const funderScorecards = funderId
    ? scorecardResultsByFunderId.value.get(funderId)
    : undefined;

  const scorecardResultsForPlacement = funderScorecards
    ? funderScorecards.filter(
        (sc) =>
          sc.score_card?.origin_offer_template_id ===
          placement.origin_offer_template_id
      )
    : [];

  return {
    ...placement,
    score_card_results: scorecardResultsForPlacement,
  } as ILenderPlacement & { score_card_results: IScorecardResult[] };
};

const filteredFunders = computed(() => {
  const { searchString, selectedRiskTiers, selectedProductTypes } =
    filters.value;
  const searchLower = searchString?.toLowerCase();
  const filtered = allFunders.value.filter((funder) => {
    const checks = [
      !searchLower || funder.full_name.toLowerCase().includes(searchLower),
      !selectedRiskTiers.length ||
        selectedRiskTiers.some((tier) =>
          (funder.risk_tiers ?? []).includes(Number(tier) as RiskTier)
        ),
      !selectedProductTypes.length ||
        selectedProductTypes.some((type) =>
          (funder.product_types ?? []).includes(Number(type) as ProductType)
        ),
    ];
    return checks.every(Boolean);
  });

  return sortFundersByScore(filtered);
});

const placementsWithScorecardResults = computed(() => {
  return placements.value.map((placement) =>
    enrichPlacementWithScorecardResults(placement)
  );
});

const createPlacementTableRow = (
  placement: ILenderPlacement & { score_card_results?: IScorecardResult[] }
): IPlacementTableRow => {
  return {
    id: placement.placement_id,
    can_send_files: placement.can_send_files,
    created_at: placement.created_at,
    funder_full_name: placement.funder?.full_name ?? "-",
    product_type: placement.product?.name || "",
    offer_template: placement.origin_offer_template?.name ?? "-",
    origin_offer_template_id: placement.origin_offer_template_id,
    client_offer_template:
      placement.destinations[0].offer_template?.name ?? "-",
    submission: submissionTypes.value?.[placement.method] || "",
    submissionId: placement.method,
    risk_tiers: placement.funder?.risk_tiers ?? [],
    tracking: placement.tracking ?? null,
    responses: placement.responses ?? [],
    status: placement.status,
    placement_id: placement.placement_id,
    isPlacement: true,
    decline_reason_id: placement.decline_reason_id,
    decline_reason: placement.decline_reason,
    decline_subreason_id: placement.decline_subreason_id,
    declinePending: placement.declinePending ?? false,
    files_sent: placement.files_sent,
    submittedBy: formatUserFullName(placement.user ?? undefined),
    offers_count: placement.offers_count,
    workflowTemplateName:
      placement.destinations[0].workflow_template?.name ?? "-",
    workflow_status: {
      status: placement.reference_workflow_status,
      sub_status: placement.reference_workflow_sub_status,
      color: placement.reference_workflow_status_color,
    },
    score_card_results: placement.score_card_results || [],
    url: placement.url ?? "",
    resubmitted_at: placement.resubmitted_at ?? null,
    can_resubmit: placement.can_resubmit ?? false,
    updated_at: placement.updated_at,
  };
};

const placementData = computed(() => {
  const searchString = filters.value.searchString?.toLowerCase();
  const filteredPlacements = filterMap(
    placementsWithScorecardResults.value,
    (placement) =>
      !searchString ||
      placement.funder?.full_name?.toLowerCase().includes(searchString),
    (placement) => createPlacementTableRow(placement)
  );

  return orderBy(filteredPlacements, "created_at", "desc");
});

const placementsByFunder = computed(() => {
  const grouped = new Map<
    string,
    { placements: IPlacementTableRow[]; funderLogo?: string }
  >();

  placementsWithScorecardResults.value.forEach((placement) => {
    const funderName = placement.funder?.full_name ?? "-";
    if (!grouped.has(funderName)) {
      grouped.set(funderName, {
        placements: [],
        funderLogo: placement.funder?.logo?.file_url,
      });
    }
  });

  placementData.value.forEach((placement) => {
    const funderName = placement.funder_full_name;
    const groupData = grouped.get(funderName);
    if (groupData) {
      groupData.placements.push(placement);
    }
  });

  return Array.from(grouped.entries()).map(
    ([funderName, { placements, funderLogo }]) => ({
      funderName,
      funderLogo,
      placements: placements.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ),
    })
  );
});

// Filter out funder tables that have no visible placements after filtering
// This keeps the page in sync with the scroll tracker (which also excludes empty funders)
const filteredPlacementsByFunder = computed(() =>
  placementsByFunder.value.filter((funderData) =>
    funderData.placements.some((placement) =>
      isValidForSubmittedPlacements(placement, filters.value)
    )
  )
);

const PLACEMENT_STATUS_TO_TYPE_MAP: Record<
  PlacementStatuses,
  PlacementCountType
> = {
  [PlacementStatuses.Approved]: "approved",
  [PlacementStatuses.Declined]: "declined",
  [PlacementStatuses.Pending]: "pending",
  [PlacementStatuses.Duplicate]: "declined",
  [PlacementStatuses.Error]: "error",
  [PlacementStatuses.PendingMissingInfoOrDocs]: "pending",
  [PlacementStatuses.PendingReview]: "pending",
  [PlacementStatuses.Queued]: "pending",
};

const placementsCountData = computed(() => {
  const data = placementsByFunder.value.reduce<
    Record<PlacementCountType, number>
  >(
    (reduced, { placements }) => {
      placements.forEach((placement) => {
        const isValid = isValidForSubmittedPlacements(placement, filters.value);
        if (isValid) {
          reduced[PLACEMENT_STATUS_TO_TYPE_MAP[placement.status]] += 1;
        }
      });
      return reduced;
    },
    { approved: 0, pending: 0, declined: 0, error: 0 }
  );

  return Object.entries(data) as Array<[PlacementCountType, number]>;
});

const totalProductsSubmitted = computed(() => placements.value.length);

const getPlacementStatusColor = (status: PlacementStatuses): UiStatusColor => {
  if (status === PlacementStatuses.Approved) {
    return "success";
  }
  if (
    [
      PlacementStatuses.Declined,
      PlacementStatuses.Duplicate,
      PlacementStatuses.Error,
    ].includes(status)
  ) {
    return "error";
  }
  return "warning";
};

const getScorecardStatusColor = (
  scorecardResult: IScorecardResult
): string | undefined => {
  return scorecardResult.score_card_status?.color;
};

const buildPlacementContentSections = (): AnchorNavigationSection[] => {
  const sections: AnchorNavigationSection[] = [];

  if (canViewPlacements.value && submittedPlacementsEl.value) {
    const submittedTablesByName = new Map<string, HTMLElement>();
    submittedPlacementsTables.value?.forEach((comp) => {
      if (comp?.funderName && comp.$el) {
        submittedTablesByName.set(comp.funderName, comp.$el);
      }
    });

    const submittedFunders = filteredPlacementsByFunder.value.reduce<
      AnchorNavigationFunder[]
    >((acc, funderData) => {
      const tableEl = submittedTablesByName.get(funderData.funderName);
      if (tableEl) {
        const products: AnchorNavigationProduct[] = [];
        funderData.placements.forEach((placement) => {
          if (isValidForSubmittedPlacements(placement, filters.value)) {
            products.push({
              name: placement.product_type || placement.offer_template || "-",
              statusColor: getPlacementStatusColor(placement.status),
            });
          }
        });

        acc.push({
          name: funderData.funderName,
          tableElement: tableEl,
          products,
        });
      }
      return acc;
    }, []);

    sections.push({
      label: t("DEALS.PLACEMENT.SUBMITTED_PLACEMENTS"),
      headerElement: submittedPlacementsEl.value,
      funders: submittedFunders,
    });
  }

  if (availableFundersEl.value) {
    const availableFunders: AnchorNavigationFunder[] = [];

    funderTableComponents.value?.forEach((comp) => {
      if (!comp?.funder?.id || !comp.$el || !comp.offerTemplates) {
        return;
      }

      // Use the same offer templates data that the table displays
      const products: AnchorNavigationProduct[] = comp.offerTemplates.map(
        (template) => {
          const scorecardColors = template.score_card_results
            ?.map((sc) => getScorecardStatusColor(sc))
            .filter((color): color is string => Boolean(color));

          return {
            name: template.name,
            scorecardColors:
              scorecardColors && scorecardColors.length > 0
                ? scorecardColors
                : undefined,
          };
        }
      );

      availableFunders.push({
        name: comp.funder.full_name,
        tableElement: comp.$el,
        products,
      });
    });

    // Sort to match the order of funders in the table
    const sortedAvailableFunders = funders.value.reduce<
      AnchorNavigationFunder[]
    >((acc, funder) => {
      const matchingFunder = availableFunders.find(
        (f) => f.name === funder.full_name
      );
      if (matchingFunder) {
        acc.push(matchingFunder);
      }
      return acc;
    }, []);

    sections.push({
      label: t("DEALS.PLACEMENT.AVAILABLE_FUNDERS_FOR_PLACEMENT"),
      headerElement: availableFundersEl.value,
      funders: sortedAvailableFunders,
    });
  }

  return sections;
};

const collapseAllSubmittedPlacementsTables = () => {
  submittedPlacementsTables.value?.forEach((tableRef) => {
    tableRef?.collapseTable();
  });
};

const collapseAllAvailableFundersTables = () => {
  funderTableComponents.value?.forEach((tableRef) => {
    tableRef?.collapseTable();
  });
};

const clearFunderTableSelections = () => {
  funderTableComponents.value?.forEach((tableRef) => {
    tableRef?.setSelectedIds([]);
    tableRef?.resetToPresets();
  });
};

const getSelectedMethods = (submissionTypes: string[]): number[] => {
  const methodMap = {
    email: PLACEMENT_METHOD.EMAIL,
    api: PLACEMENT_METHOD.API,
    manual: PLACEMENT_METHOD.MANUAL,
    lendflow: PLACEMENT_METHOD.LENDFLOW,
  };

  return submissionTypes.reduce((acc, type) => {
    const method = methodMap[type as keyof typeof methodMap];
    if (method) {
      acc.push(method);
    }
    return acc;
  }, [] as number[]);
};

const getRowPlacementMethod = (
  rowId: string,
  baseFunder: IFunder,
  funderRowStates?: Record<string, RowState>
): number => {
  const rowState = funderRowStates?.[rowId];
  return rowState?.selected_method ?? baseFunder.primary_method;
};

const filterRowsByMethods = (
  selectedRows: string[],
  selectedMethods: number[],
  baseFunder: IFunder,
  funderRowStates?: Record<string, RowState>
): string[] => {
  return selectedRows.filter((rowId) => {
    const method = getRowPlacementMethod(rowId, baseFunder, funderRowStates);
    return selectedMethods.includes(method);
  });
};

const updateFunderData = (
  funderId: string,
  filteredRows: string[],
  funderRowStates?: Record<string, RowState>,
  funderTemplateData?: EnrichedTableRow[]
): void => {
  if (!filteredRows.length) {
    selectedRowsByFunder.value.delete(funderId);
    rowStatesByFunder.value.delete(funderId);
    templateDataByFunder.value.delete(funderId);
    return;
  }

  selectedRowsByFunder.value.set(funderId, filteredRows);

  const filteredRowStates = filteredRows.reduce(
    (acc, rowId) => {
      if (funderRowStates?.[rowId]) {
        acc[rowId] = funderRowStates[rowId];
      }
      return acc;
    },
    {} as Record<string, RowState>
  );
  rowStatesByFunder.value.set(funderId, filteredRowStates);

  if (funderTemplateData) {
    const filteredTemplateData = funderTemplateData.filter((template) =>
      filteredRows.includes(template.id)
    );
    templateDataByFunder.value.set(funderId, filteredTemplateData);
  }
};

const clearAllSelections = (): void => {
  selectedRowsByFunder.value.clear();
  rowStatesByFunder.value.clear();
  templateDataByFunder.value.clear();
  selectedFunderIds.value = [];
};

const alignSubmissionsWithSelectedSubmissionTypes = (values: string[]) => {
  const selectedMethods = getSelectedMethods(values);

  if (!selectedMethods.length) {
    clearAllSelections();
    return;
  }

  const fundersById = new Map(funders.value.map((f) => [f.id, f]));

  selectedRowsByFunder.value.forEach((selectedRows, funderId) => {
    const baseFunder = fundersById.get(funderId);
    const funderRowStates = rowStatesByFunder.value.get(funderId);
    const funderTemplateData = templateDataByFunder.value.get(funderId);

    if (baseFunder && funderTemplateData) {
      const filteredRows = filterRowsByMethods(
        selectedRows,
        selectedMethods,
        baseFunder,
        funderRowStates
      );

      updateFunderData(
        funderId,
        filteredRows,
        funderRowStates,
        funderTemplateData
      );
    }
  });

  selectedFunderIds.value = Array.from(selectedRowsByFunder.value.keys());
};

const resetFunderSumbissionProperties = () => {
  funders.value = funders.value.map((funder) => ({
    ...funder,
    offer_template: null,
    submission: String(funder.primary_method),
    workflow_template_id: undefined,
    client_offer_template: undefined,
  }));
};

const handlePlacementSubmitted = () => {
  trackEvent(AnalyticsEvent.PlacementSubmitted, {
    deal_id: activeDeal.value.id,
    funders_count: selectedFunderIds.value.length,
  });
  clearSelectedRows();
};

const clearSelectedRows = () => {
  resetFunderSumbissionProperties();
  clearFunderTableSelections();
  selectedFunderIds.value = [];
  selectedRowsByFunder.value.clear();
  rowStatesByFunder.value.clear();
  templateDataByFunder.value.clear();
};

const clearSuccessfulRows = (
  successfulRows: Array<{ funderId: string; rowId: string }>
) => {
  successfulRows.forEach(({ funderId, rowId }) => {
    const currentSelectedRows = selectedRowsByFunder.value.get(funderId) || [];
    const updatedRows = currentSelectedRows.filter(
      (selectedRowId) => selectedRowId !== rowId
    );

    if (updatedRows.length === 0) {
      selectedRowsByFunder.value.delete(funderId);
      rowStatesByFunder.value.delete(funderId);
      templateDataByFunder.value.delete(funderId);
    } else {
      selectedRowsByFunder.value.set(funderId, updatedRows);

      const currentRowStates = rowStatesByFunder.value.get(funderId) || {};
      const updatedRowStates = { ...currentRowStates };
      delete updatedRowStates[rowId];
      rowStatesByFunder.value.set(funderId, updatedRowStates);

      const currentTemplateData =
        templateDataByFunder.value.get(funderId) || [];
      const updatedTemplateData = currentTemplateData.filter(
        (template) => template.id !== rowId
      );
      templateDataByFunder.value.set(funderId, updatedTemplateData);
    }
  });

  selectedFunderIds.value = Array.from(selectedRowsByFunder.value.keys());

  clearFunderTableSelections();
};

const { fetchWrapper: getFunderScorecardResults } = usePromiseWrapper(
  async (ids: string[]) => {
    if (!activeDeal.value.id) {
      return;
    }
    const response: IScorecardResult[] =
      await applicationsApiService.getScorecardsResults({
        id: activeDeal.value.id,
        funderIds: ids,
      });
    scorecardResults.value = response;
  }
);

const enrichFunderWithScorecardResults = (
  funder: IFunder,
  initialSetup?: boolean
) => {
  const funderScorecards = scorecardResultsByFunderId.value.get(funder.id);
  if (!funderScorecards?.length) {
    return { ...funder, score_card_results: [] };
  }

  const scorecardResultsForFunder = initialSetup
    ? funderScorecards.filter((sc) => sc.score_card?.origin_offer_template_id)
    : funderScorecards.filter(
        (sc) =>
          sc.score_card?.origin_offer_template_id === funder.offer_template?.id
      );

  if (!scorecardResultsForFunder.length) {
    return { ...funder, score_card_results: [] };
  }

  scorecardResultsForFunder.sort((a, b) => {
    const relativeScoreA = Number(a.points ?? -1) / (a.score_card?.points ?? 1);
    const relativeScoreB = Number(b.points ?? -1) / (b.score_card?.points ?? 1);
    return relativeScoreB - relativeScoreA;
  });

  return {
    ...funder,
    workflow_template_id:
      scorecardResultsForFunder[0]?.score_card?.workflow_template?.id,
    client_offer_template:
      scorecardResultsForFunder[0]?.score_card?.offer_template_id ?? "",
    offer_template: {
      id:
        scorecardResultsForFunder[0]?.score_card?.origin_offer_template_id ??
        "",
    },
    score_card_results: scorecardResultsForFunder,
  };
};

const handleSelectedRows = (
  funderId: string,
  selectedRows: string[],
  rowStatesData: Record<string, RowState>,
  selectedTemplateData: EnrichedTableRow[]
) => {
  trackEvent(AnalyticsEvent.PlacementFunderSelected, {
    funder_id: funderId,
    selected_count: selectedRows.length,
  });

  if (!selectedRows.length) {
    selectedRowsByFunder.value.delete(funderId);
    rowStatesByFunder.value.delete(funderId);
    templateDataByFunder.value.delete(funderId);
    selectedFunderIds.value = Array.from(selectedRowsByFunder.value.keys());
    return;
  }

  selectedRowsByFunder.value.set(funderId, selectedRows);
  rowStatesByFunder.value.set(funderId, rowStatesData);
  templateDataByFunder.value.set(funderId, selectedTemplateData);
  selectedFunderIds.value = Array.from(selectedRowsByFunder.value.keys());
};

const calculateFunderScore = (funder: IFunder): number => {
  const points = funder.score_card_results?.[0]?.points ?? -1;
  const totalPoints = funder.score_card_results?.[0]?.score_card?.points ?? 1;
  return Number.parseFloat(String(points)) / totalPoints;
};

const sortFundersByScore = (funders: IFunder[]): IFunder[] =>
  [...funders].sort((a, b) => {
    const scoreA = calculateFunderScore(a);
    const scoreB = calculateFunderScore(b);
    if (scoreA !== scoreB) {
      return scoreB - scoreA;
    }
    return a.full_name.localeCompare(b.full_name);
  });

watch(
  [() => filteredFunders.value],
  ([newFilteredFunders]) => {
    const offerFundersById = new Map(
      allSelectedOfferFunders.value.map((f) => [f.id, f])
    );

    funders.value = newFilteredFunders.filter((funder) => {
      const funderInOfferStage = offerFundersById.get(funder.id);
      const hasCustomOfferTemplates = funderInOfferStage?.offer_templates.some(
        (template) => template.enabled
      );
      return hasCustomOfferTemplates || Boolean(funder.product_types?.length);
    });
  },
  { immediate: true }
);

watch(
  [funders, filteredPlacementsByFunder, scorecardResults, canViewPlacements],
  () => {
    placementContentSections.value = buildPlacementContentSections();
  },
  { flush: "post", immediate: true, deep: true }
);

onMounted(async () => {
  trackEvent(AnalyticsEvent.PlacementSectionViewed, {
    deal_id: activeDeal.value.id,
  });
  anchorScrollContainer.value = document.querySelector<HTMLElement>(
    "#dealsWrapper > .overflow-auto"
  );
  funderIds.value = activeStage.value?.funders as string[];
  await Promise.all([
    getFunderScorecardResults(funderIds.value),
    dispatch("applications/getPlacements"),
  ]);

  const { value: placementOnboardingSeen } = await keyValueService.kvRequest<
    boolean | null
  >(KeyId.PlacementWflOnboardingTourSeen);

  if (!placementOnboardingSeen) {
    setTimeout(() => {
      nextTourStep();
    }, 1_000);
  }
});

onBeforeRouteLeave(() => {
  removeCurrentStep();
});
</script>
